// Унифицированный бейдж рейтингов организации (Yandex / 2GIS / Google).
// Читает /ratings.json (генерируется cron'ом rating-sync на сервере, gitignored).
// При отсутствии или невалидности данных рендерит дефолтные значения, так что
// компонент никогда не показывает пустоту.
//
// Дизайн соответствует Figma-макету (node 6697:594202):
//   • Иконки 18×18 (Yandex Карты — image-PNG embed, 2ГИС — vector path, Google — 4-цв. G)
//   • Оценка 15px Bold #202020, в 24×24 контейнере с line-height 1.7
//   • Звёзды 24×24 жёлтые с overlap -10px (5 шт)
//   • Подпись 8px Regular #515151, для 2ГИС inline-ссылка «Оставить отзыв» 8px Medium #5CB853
//   • Шрифт Nunito Sans (default через tailwind.config)
//   • Высота блока 44px, gap 4px между строкой и подписью, gap 6px внутри строки
//   • Desktop: gap 48px между блоками в строку
//   • Mobile: gap 12px между блоками в столбец

import { useEffect, useState } from 'react'

type Source = 'yandex' | 'twogis' | 'google'

type RatingsJson = {
  updated_at?: string
  yandex?: { rating?: number; ok?: boolean }
  twogis?: { rating?: number; ok?: boolean }
  google?: { rating?: number; ok?: boolean }
}

const DEFAULTS: Record<Source, number> = {
  yandex: 5.0,
  twogis: 5.0,
  google: 4.9,
}

const URLS: Record<Source, string> = {
  yandex: 'https://yandex.ru/maps/org/1286099425/reviews',
  twogis: 'https://2gis.ru/moscow/firm/70000001021746963',
  google: 'https://share.google/HaU92qXPEVrtgVYuO',
}

// Прямая ссылка на форму отзыва 2ГИС.
const TWOGIS_REVIEW_URL =
  'https://2gis.ru/moscow/firm/70000001021746963/tab/reviews/addreview'

const CAPTIONS: Record<Source, string> = {
  yandex: 'Рейтинг организации в Яндексе',
  twogis: 'Оценка в 2ГИС',
  google: 'Рейтинг организации в Google',
}

// Иконки Я и 2ГИС — реальные SVG из Figma, лежат в /public/icons/ratings/.
// Google — inline 4-цветный официальный G (по запросу владельца проекта).
const ICON_PATHS: Record<'yandex' | 'twogis', string> = {
  yandex: '/icons/ratings/yandex-maps.svg',
  twogis: '/icons/ratings/2gis.svg',
}

const GoogleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 48 48' aria-hidden='true' className='flex-shrink-0'>
    <path fill='#4285F4' d='M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z' />
    <path fill='#34A853' d='M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z' />
    <path fill='#FBBC05' d='M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z' />
    <path fill='#EA4335' d='M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z' />
  </svg>
)

const renderIcon = (s: Source) => {
  if (s === 'google') return <GoogleIcon />
  return (
    <img
      src={ICON_PATHS[s]}
      alt=''
      width={16}
      height={16}
      className='flex-shrink-0'
      aria-hidden='true'
    />
  )
}

// 5 жёлтых звёзд 16×16 с gap 2px и закруглёнными концами (через stroke
// с тем же цветом и stroke-linejoin="round" — стандартный приём для
// rounded-star без отдельного path).
const Stars = () => (
  <div className='flex items-center gap-0.5'>
    {[0, 1, 2, 3, 4].map((i) => (
      <svg
        key={i}
        width='13'
        height='13'
        viewBox='0 0 24 24'
        aria-hidden='true'
        className='flex-shrink-0'
      >
        <polygon
          points='12,2 15,9 22,9 16.5,13.5 18.5,21 12,17 5.5,21 7.5,13.5 2,9 9,9'
          fill='#FFB400'
          stroke='#FFB400'
          strokeWidth='2'
          strokeLinejoin='round'
          strokeLinecap='round'
        />
      </svg>
    ))}
  </div>
)

type Props = {
  variant: 'dsk' | 'mbl'
}

const RatingsBadge = ({ variant }: Props) => {
  const [ratings, setRatings] = useState<Record<Source, number>>(DEFAULTS)

  useEffect(() => {
    let cancelled = false
    fetch('/ratings.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: RatingsJson | null) => {
        if (!data || cancelled) return
        setRatings({
          yandex:
            data.yandex?.ok && typeof data.yandex.rating === 'number'
              ? data.yandex.rating
              : DEFAULTS.yandex,
          twogis:
            data.twogis?.ok && typeof data.twogis.rating === 'number'
              ? data.twogis.rating
              : DEFAULTS.twogis,
          google:
            data.google?.ok && typeof data.google.rating === 'number'
              ? data.google.rating
              : DEFAULTS.google,
        })
      })
      .catch(() => {
        /* fallback — оставляем DEFAULTS */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const sources: Source[] = ['yandex', 'twogis', 'google']
  const isMbl = variant === 'mbl'

  // Desktop — flex-row gap 32px (Pixel Perfect Figma).
  // Mobile — flex-wrap c justify-center. Поведение:
  //   • Когда все 3 блока влезают в строку → центрируется группа из трёх.
  //   • Когда влезает только 2 → row 1 = 2 блока, row 2 = Google один и
  //     автоматически центрируется (justify-center раскидывает по центру).
  //   • На очень узких экранах — все три в колонку, по центру.
  const wrapperClass = isMbl
    ? 'flex flex-wrap justify-center gap-3 items-center w-full'
    : 'flex flex-row gap-8 items-center'

  return (
    <div className={wrapperClass}>
      {sources.map((s) => {
        const rating = ratings[s]
        // По Figma: Yandex — «5,0» (запятая), 2ГИС — «5.0» (точка), Google — «4,9».
        const ratingStr =
          s === 'twogis' ? rating.toFixed(1) : rating.toFixed(1).replace('.', ',')

        return (
          <div
            key={s}
            className={`flex flex-col gap-1 justify-center h-11 ${
              isMbl ? 'items-center' : 'items-start'
            }`}
          >
            <a
              href={URLS[s]}
              target='_blank'
              rel='noopener noreferrer'
              className='flex gap-1.5 items-center hover:opacity-80'
              aria-label={`${CAPTIONS[s]}: ${ratingStr} из 5`}
            >
              {renderIcon(s)}
              <div className='flex items-center gap-1.5'>
                <div className='text-[15px] font-bold text-dark tabular-nums leading-none'>
                  {ratingStr}
                </div>
                <Stars />
              </div>
            </a>

            {s === 'twogis' ? (
              !isMbl && (
                <div className='flex gap-1.5 h-3 items-center'>
                  <a
                    href={URLS.twogis}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[8px] font-normal text-[#515151] whitespace-nowrap hover:opacity-80'
                  >
                    {CAPTIONS.twogis}
                  </a>
                  <a
                    href={TWOGIS_REVIEW_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[8px] font-medium text-[#5CB853] whitespace-nowrap hover:underline'
                    style={{ lineHeight: 1.7 }}
                  >
                    Оставить отзыв
                  </a>
                </div>
              )
            ) : (
              !isMbl && (
                <a
                  href={URLS[s]}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-[8px] font-normal text-[#515151] whitespace-nowrap hover:opacity-80'
                >
                  {CAPTIONS[s]}
                </a>
              )
            )}
          </div>
        )
      })}
    </div>
  )
}

export default RatingsBadge
